/**
 * Доменные типы сквозного примитива «Электронные заявки между функциями»
 * (requests-core, ADR-0016). Зеркало DTO роутов `/api/v1/requests/*` и
 * `osnova_core::requests` / `osnova_storage::requests`. snake_case 1:1 с backend.
 */

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/** Заявка между функциями (конверт + типозависимый payload). */
export interface FunctionRequest {
  id: string;
  /** aosr | geodesy | lab | ndt | mto_supply. */
  request_type: string;
  source_function: string;
  target_function: string;
  /** draft|submitted|accepted|rejected|in_progress|completed|closed|cancelled. */
  status: string;
  /** low|normal|high|urgent. */
  priority: string;
  title: string;
  project_id: string | null;
  site_id: string | null;
  element_id: string | null;
  payload: unknown;
  due_date: string | null;
  /** Вид результата-артефакта (заполняется при прикреплении результата). */
  result_kind: string | null;
  /** Полиморфная ссылка на артефакт (напр. executive_act_id для aosr). */
  result_ref: string | null;
  linked_requests: unknown;
  needs_review: boolean;
  initiator_user_id: string | null;
  client_uuid: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** Когда SLA-поллер отправил напоминание о приближении срока (§7). */
  sla_reminded_at: string | null;
  /** Когда SLA-поллер зафиксировал просрочку срока (§7). */
  sla_overdue_at: string | null;
}

/** Запись истории переходов заявки (карточка + аудит). */
export interface RequestTransition {
  id: string;
  request_id: string;
  from_status: string | null;
  to_status: string;
  actor_user_id: string | null;
  reason: string | null;
  created_at: string;
}

/** Ответ исполнителя по заявке (текст + необязательный файл) — результат
 *  заявок, где результат и есть ответ (`mto_supply`). Зеркало
 *  `osnova_storage::requests::RequestResponse`. */
export interface RequestResponse {
  id: string;
  request_id: string;
  body: string;
  file_name: string | null;
  file_mime: string | null;
  file_size: number | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

/** Тип «Заявка в снабжение» — код в `request_type`. */
export const MTO_SUPPLY = 'mto_supply';

/** Строка материала в заявке в снабжение — свободный текст. */
export interface SupplyMaterial {
  name: string;
  unit: string | null;
  quantity: string | null;
}

/** Документ РД заявки — снимок сервера на момент подачи. */
export interface SupplyDocument {
  revision_id: string;
  /** Шифр документа. */
  code: string;
  title: string | null;
  /** Ревизия словами («Изм.2», «ревизия 3»). */
  revision: string | null;
}

/** Payload заявки в снабжение (зеркало `osnova_core::requests::MtoSupplyPayload`). */
export interface MtoSupplyPayload {
  revision_ids: string[];
  documents: SupplyDocument[];
  /** Название объекта на момент подачи. */
  project_name: string | null;
  /** Кто подал (ФИО) — снимок сервера. */
  initiator_name: string | null;
  materials: SupplyMaterial[];
  /** К какой дате материалы нужны на объекте (`YYYY-MM-DD`). */
  need_by: string | null;
  comment: string | null;
}

/** Типы заявок для форм и фильтров (реестр маршрутов — на backend). */
export const REQUEST_TYPES: { value: string; label: string }[] = [
  { value: 'aosr', label: 'АОСР (освидетельствование)' },
  { value: 'geodesy', label: 'Геодезия' },
  { value: 'lab', label: 'Лаборатория' },
  { value: 'ndt', label: 'Неразрушающий контроль' },
  { value: MTO_SUPPLY, label: 'Заявка в снабжение' },
  { value: 'budget_limit_exceeded', label: 'ПЭО: превышение лимита бюджета' },
  { value: 'contract_price_revision', label: 'Юрслужба: пересмотр твёрдой цены договора (ДС)' },
  { value: 'staffing_request', label: 'Кадры: запрос на усиление бригады / подбор персонала' },
  { value: 'meeting_assignment', label: 'Совещания: поручение протокола' },
  { value: 'legal_contract_review', label: 'Юрслужба: экспертиза договора / ДС' },
  { value: 'legal_claim', label: 'Юрслужба: подготовка претензии' },
  { value: 'legal_letter', label: 'Юрслужба: официальное письмо' },
  { value: 'legal_poa', label: 'Юрслужба: оформление доверенности' },
  { value: 'legal_counterparty_check', label: 'Юрслужба: проверка контрагента' },
];

/** Типы заявок, создаваемые вручную через форму /requests (поручения совещаний создаются из протокола). */
export const CREATABLE_REQUEST_TYPES = REQUEST_TYPES.filter(
  (t) => t.value !== 'meeting_assignment',
);

export function isLegalRequest(requestType: string): boolean {
  return requestType.startsWith('legal_') || requestType === 'contract_price_revision';
}

/** Принимает ли заявка текстовый ответ исполнителя (RespondForm) для автоматического выполнения. */
export function takesTextResponse(requestType: string): boolean {
  return (
    requestType === MTO_SUPPLY ||
    requestType === 'budget_limit_exceeded' ||
    requestType === 'staffing_request' ||
    requestType === 'meeting_assignment' ||
    isLegalRequest(requestType)
  );
}

/** Допустимые коды source_function для выбранного типа заявки (зеркало RequestType::initiators). */
export function allowedSourceFunctionsForType(requestType: string): string[] {
  switch (requestType) {
    case 'aosr':
    case 'lab':
    case 'ndt':
      return ['field', 'pto', 'ksp'];
    case 'geodesy':
      return ['field', 'pto', 'econ', 'ksp'];
    case MTO_SUPPLY:
      return ['pto', 'field', 'ksp'];
    case 'budget_limit_exceeded':
      return ['mto', 'smeta', 'pto', 'progress', 'field'];
    case 'contract_price_revision':
      return ['econ', 'pto', 'smeta'];
    case 'staffing_request':
      return ['field', 'pto', 'ksp', 'progress', 'econ', 'mto', 'logist', 'geodesy'];
    default:
      return ['field', 'pto', 'mto', 'econ', 'smeta', 'ksp', 'logist', 'geodesy', 'progress'];
  }
}

/** Функции-инициаторы. */
export const SOURCE_FUNCTIONS: { value: string; label: string }[] = [
  { value: 'field', label: 'Прораб (площадка)' },
  { value: 'pto', label: 'ПТО (инженер)' },
  { value: 'mto', label: 'Снабжение (МТО)' },
  { value: 'econ', label: 'Экономист (ПЭО)' },
  { value: 'smeta', label: 'Сметчик' },
  { value: 'ksp', label: 'Планировщик (КСП)' },
  { value: 'logist', label: 'Логистика' },
  { value: 'progress', label: 'Руководитель проекта' },
  { value: 'geodesy', label: 'Геодезия' },
  { value: 'kadry', label: 'Отдел кадров' },
  { value: 'meetings', label: 'Совещания' },
  { value: 'statut', label: 'Юрслужба' },
];

export const PRIORITIES: { value: string; label: string }[] = [
  { value: 'low', label: 'низкий' },
  { value: 'normal', label: 'обычный' },
  { value: 'high', label: 'высокий' },
  { value: 'urgent', label: 'срочный' },
];

/** Target-функции для реестра «Входящие». В скобках — агент-исполнитель:
 *  заявки на геодезию с AI-GEO-2026 обрабатывает ОСНОВА.Геодезист (не ПТО). */
export const TARGET_FUNCTIONS: { value: string; label: string }[] = [
  { value: 'pto_act', label: 'АОСР (Инженер ПТО)' },
  { value: 'pto', label: 'ПТО (Инженер)' },
  { value: 'geodesy', label: 'Геодезия (Геодезист)' },
  { value: 'lab', label: 'Лаборатория' },
  { value: 'ndt', label: 'Неразрушающий контроль' },
  { value: 'mto', label: 'Снабжение (МТО)' },
  { value: 'econ', label: 'ПЭО (Экономист)' },
  { value: 'smeta', label: 'Сметный отдел (Сметчик)' },
  { value: 'ksp', label: 'Планирование (КСП)' },
  { value: 'field', label: 'Стройплощадка (Прораб)' },
  { value: 'logist', label: 'Логистика (Логист)' },
  { value: 'kadry', label: 'Отдел кадров (Кадры)' },
  { value: 'statut', label: 'Юрслужба (Юрист)' },
  { value: 'progress', label: 'Руководитель проекта' },
];

/** Функция словами — для маршрута «откуда → куда» в карточке и списках. */
export function functionLabel(code: string): string {
  switch (code) {
    case 'pto':
      return 'ПТО';
    case 'field':
      return 'Прораб';
    case 'mto':
      return 'Снабжение';
    case 'geodesy':
      return 'Геодезия';
    case 'pto_act':
      return 'ПТО (АОСР)';
    case 'lab':
      return 'Лаборатория';
    case 'ndt':
      return 'Неразрушающий контроль';
    case 'statut':
      return 'Юрслужба';
    case 'econ':
    case 'peo':
      return 'Экономист';
    case 'smeta':
      return 'Сметчик';
    case 'ksp':
      return 'КСП';
    case 'logist':
      return 'Логист';
    case 'kadry':
      return 'Кадры';
    case 'meetings':
      return 'Совещания';
    case 'progress':
      return 'Руководитель проекта';
    default:
      return code;
  }
}

/** Заявка ещё в работе у исполнителя (подана, принята или выполняется). */
export function isOpenRequest(status: string): boolean {
  return status === 'submitted' || status === 'accepted' || status === 'in_progress';
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function text(v: unknown): string | null {
  if (typeof v === 'number') return String(v);
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/**
 * Payload заявки в снабжение из произвольного JSON. Сервер нормализует
 * payload при создании, но карточка не должна падать на чужих/старых данных —
 * всё неизвестное отбрасывается.
 */
export function readMtoSupplyPayload(payload: unknown): MtoSupplyPayload {
  const p = isRecord(payload) ? payload : {};
  const revisionIds = Array.isArray(p.revision_ids)
    ? p.revision_ids.filter((x): x is string => typeof x === 'string' && x.length > 0)
    : [];
  const documents: SupplyDocument[] = Array.isArray(p.documents)
    ? p.documents.filter(isRecord).flatMap((d) => {
        const revisionId = text(d.revision_id);
        const code = text(d.code);
        return revisionId && code
          ? [{ revision_id: revisionId, code, title: text(d.title), revision: text(d.revision) }]
          : [];
      })
    : [];
  const materials: SupplyMaterial[] = Array.isArray(p.materials)
    ? p.materials.filter(isRecord).flatMap((m) => {
        const name = text(m.name);
        return name ? [{ name, unit: text(m.unit), quantity: text(m.quantity) }] : [];
      })
    : [];
  return {
    revision_ids: revisionIds,
    documents,
    project_name: text(p.project_name),
    initiator_name: text(p.initiator_name),
    materials,
    need_by: text(p.need_by),
    // Заявки из раздела ПТО «Заявки» до исправления моста несли текст в `body`.
    comment: text(p.comment) ?? text(p.body),
  };
}

/**
 * Ссылка «Сформировать ведомость» у снабженца: модуль извлечения материалов
 * из РД с объектом и ревизиями заявки. `null` — нечего формировать (нет
 * объекта или документов РД).
 */
export function mtoSupplyRunsHref(r: FunctionRequest): string | null {
  if (r.request_type !== MTO_SUPPLY || !r.project_id) return null;
  const { revision_ids: revisions } = readMtoSupplyPayload(r.payload);
  if (revisions.length === 0) return null;
  // Ревизии через запятую как есть (не %2C): так адрес читается глазами, а
  // `searchParams.get('revisions')` на странице модуля вернёт то же самое.
  const project = encodeURIComponent(r.project_id);
  const ids = revisions.map(encodeURIComponent).join(',');
  return `/agents/mto/runs?module=rd_extract&project=${project}&revisions=${ids}`;
}

/** Статусы для фильтров (сводный реестр). Порядок — по ходу автомата. */
export const REQUEST_STATUSES: { value: string; label: string }[] = [
  { value: 'draft', label: 'черновик' },
  { value: 'submitted', label: 'подана' },
  { value: 'accepted', label: 'принята' },
  { value: 'rejected', label: 'возвращена' },
  { value: 'in_progress', label: 'в работе' },
  { value: 'completed', label: 'выполнена' },
  { value: 'closed', label: 'закрыта' },
  { value: 'cancelled', label: 'отменена' },
];

export function requestTypeLabel(t: string): string {
  return REQUEST_TYPES.find((x) => x.value === t)?.label ?? t;
}

export function statusLabel(s: string): string {
  switch (s) {
    case 'draft':
      return 'черновик';
    case 'submitted':
      return 'подана';
    case 'accepted':
      return 'принята';
    case 'rejected':
      return 'возвращена';
    case 'in_progress':
      return 'в работе';
    case 'completed':
      return 'выполнена';
    case 'closed':
      return 'закрыта';
    case 'cancelled':
      return 'отменена';
    default:
      return s;
  }
}

export function statusTone(s: string): Tone {
  switch (s) {
    case 'closed':
    case 'completed':
      return 'success';
    case 'rejected':
    case 'cancelled':
      return 'danger';
    case 'accepted':
    case 'in_progress':
      return 'info';
    case 'submitted':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function priorityLabel(p: string): string {
  return PRIORITIES.find((x) => x.value === p)?.label ?? p;
}

export function priorityTone(p: string): Tone {
  switch (p) {
    case 'urgent':
      return 'danger';
    case 'high':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Доступное действие перехода (зеркало backend `target_status` + автомата). */
export interface RequestAction {
  action: string;
  label: string;
  /** Требует ввода причины (reject). */
  needsReason?: boolean;
  /** Требует прикреплённого результата (complete). */
  needsResult?: boolean;
}

/**
 * Действия, доступные из текущего статуса (зеркало `target_status` + `rule_for`).
 * `cancel` — только из не-терминальных не-completed (draft/submitted/accepted/
 * in_progress), т.к. автомат отсекает cancel из completed/rejected.
 */
export function actionsForStatus(status: string): RequestAction[] {
  switch (status) {
    case 'draft':
      return [
        { action: 'submit', label: 'Подать' },
        { action: 'cancel', label: 'Отменить' },
      ];
    case 'submitted':
      return [
        { action: 'accept', label: 'Принять' },
        { action: 'reject', label: 'Вернуть', needsReason: true },
        { action: 'cancel', label: 'Отменить' },
      ];
    case 'accepted':
      return [
        { action: 'progress', label: 'В работу' },
        { action: 'cancel', label: 'Отменить' },
      ];
    case 'rejected':
      return [{ action: 'submit', label: 'Подать повторно' }];
    case 'in_progress':
      return [
        { action: 'complete', label: 'Завершить', needsResult: true },
        { action: 'cancel', label: 'Отменить' },
      ];
    case 'completed':
      return [{ action: 'close', label: 'Закрыть' }];
    default:
      return [];
  }
}

/**
 * SLA-бейдж по сроку (§7). Считается на клиенте от `due_date` + статуса (виден
 * даже если серверный поллер выключен): для активных (submitted/accepted/
 * in_progress) — «просрочена» при истёкшем сроке, «скоро срок» в пределах 3 дней.
 * Иначе — `null` (бейджа нет). Серверный поллер поверх этого ставит
 * `needs_review` и пишет audit.
 */
export function slaBadge(r: FunctionRequest): { label: string; tone: Tone } | null {
  const active = ['submitted', 'accepted', 'in_progress'].includes(r.status);
  if (!active || !r.due_date) return null;
  const due = new Date(r.due_date);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return { label: 'просрочена', tone: 'danger' };
  if (diffDays <= 3) return { label: 'скоро срок', tone: 'warning' };
  return null;
}

/** Дата ISO → `01.07.2026`. Пусто/невалид → прочерк/как есть. Дата без
 *  времени (`2026-07-01`, срок заявки) — как есть, без сдвига часового пояса. */
export function fmtRequestDate(value: string | null | undefined): string {
  if (!value) return '—';
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (day) return `${day[3]}.${day[2]}.${day[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
